import {Thermometer, Flame, Wind, Calculator} from 'lucide-react';

import SEO from "../components/SEO";
import InteractiveGWPComparison from "../components/InteractiveGWPComparison";
import GreenhouseEffectAnimation from "../components/GreenhouseEffectAnimation";
import GasLifetimeVisualization from "../components/GasLifetimeVisualization";

export default function LearnScience() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Greenhouse Gas Calculator - Learn the Science",
    "url": "https://www.greenhousegascalculator.com/science",
    "description": "Educational hub explaining greenhouse effect calculator, global warming potential (GWP), and the science behind methane vs CO2 emissions.",
    "educationalLevel": "Beginner to Intermediate",
    "teaches": [
      "Greenhouse Effect",
      "Global Warming Potential",
      "CO2 Equivalents",
      "Methane vs Carbon Dioxide"
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Global Warming Potential (GWP)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Global Warming Potential (GWP) measures how much heat a greenhouse gas traps in the atmosphere compared to CO2 over a specific time period (usually 100 years). For example, methane has a GWP of 28, meaning it's 28 times more potent than CO2 over 100 years."
        }
      },
      {
        "@type": "Question",
        "name": "Why is methane more harmful than CO2?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Methane (CH4) traps 28 times more heat than CO2 over 100 years. While methane breaks down faster in the atmosphere (9-12 years vs centuries for CO2), its immediate warming effect is much stronger, making it a critical target for short-term climate action."
        }
      },
      {
        "@type": "Question",
        "name": "What is CO2 equivalent (CO2e)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CO2 equivalent (CO2e) is a standard unit that converts all greenhouse gases to the equivalent amount of CO2 based on their Global Warming Potential. This allows us to compare different gases on a single scale."
        }
      }
    ]
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Learn the Science | Greenhouse Effect Calculator & Global Warming Potential"
        description="Educational hub explaining greenhouse effect calculator, global warming potential (GWP), and the science behind methane vs CO2. Understand CO2 equivalents and emission calculations."
        keywords="greenhouse effect calculator, global warming potential, GWP, methane vs CO2, CO2 equivalent, CO2e, greenhouse gas science, emission factors"
        canonical="https://www.greenhousegascalculator.com/science"
        schema={faqSchema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-green-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Thermometer className="w-10 h-10 text-[#2E8B57]" />
              <h1 className="text-4xl font-bold text-gray-900">
                Learn the Science
              </h1>
            </div>
            <p className="text-xl text-gray-600">
              Understand the greenhouse effect, global warming potential, and the science behind carbon footprint calculations
            </p>
          </div>
        </div>
      </section>

      {/* Greenhouse Effect Calculator Explanation */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-[#2E8B57]" />
              <h2 className="text-2xl font-bold text-gray-900">
                How the Greenhouse Effect Calculator Works
              </h2>
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-600 leading-relaxed mb-4">
                The greenhouse effect is a natural process where certain gases in Earth's atmosphere trap heat from the sun, keeping our planet warm enough to support life. However, human activities have increased the concentration of these gases, leading to enhanced warming.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our greenhouse effect calculator measures the amount of greenhouse gases you produce through daily activities and converts them into carbon dioxide equivalents (CO₂e) to provide a standardized measurement of your climate impact.
              </p>
            </div>
          </div>
          
          {/* Interactive Animation */}
          <GreenhouseEffectAnimation />
        </div>
      </section>

      {/* Global Warming Potential */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Understanding Global Warming Potential (GWP)
          </h2>
          
          <p className="text-gray-600 leading-relaxed mb-8">
            Global Warming Potential is a measure of how much heat a greenhouse gas traps in the atmosphere compared to carbon dioxide over a specific time period (usually 100 years). Different gases have different warming effects.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* CO2 */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Wind className="w-6 h-6 text-gray-600" />
                <h3 className="font-bold text-gray-900">Carbon Dioxide</h3>
              </div>
              <div className="mb-4">
                <p className="text-3xl font-bold text-[#2E8B57]">GWP: 1</p>
                <p className="text-sm text-gray-500">(baseline)</p>
              </div>
              <p className="text-sm text-gray-600">
                CO₂ is the reference gas against which all other greenhouse gases are measured. It remains in the atmosphere for hundreds of years.
              </p>
            </div>

            {/* Methane */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-gray-900">Methane (CH₄)</h3>
              </div>
              <div className="mb-4">
                <p className="text-3xl font-bold text-orange-600">GWP: 28-36</p>
                <p className="text-sm text-gray-500">(100-year)</p>
              </div>
              <p className="text-sm text-gray-600">
                Methane is 28-36 times more potent than CO₂ but breaks down faster, staying in the atmosphere for about 12 years.
              </p>
            </div>

            {/* N2O */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Wind className="w-6 h-6 text-blue-600" />
                <h3 className="font-bold text-gray-900">Nitrous Oxide (N₂O)</h3>
              </div>
              <div className="mb-4">
                <p className="text-3xl font-bold text-blue-600">GWP: 265</p>
                <p className="text-sm text-gray-500">(100-year)</p>
              </div>
              <p className="text-sm text-gray-600">
                N₂O is 265 times more potent than CO₂ and can remain in the atmosphere for over 100 years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive GWP Tool */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <InteractiveGWPComparison />
        </div>
      </section>

      {/* Gas Lifetime Visualization */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <GasLifetimeVisualization />
        </div>
      </section>

      {/* Methane vs CO2 Math */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            The Math Behind Methane vs. CO₂
          </h2>

          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Converting Methane to CO₂ Equivalents
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To calculate the CO₂ equivalent (CO₂e) of methane emissions, we multiply the amount of methane by its Global Warming Potential:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg font-mono text-sm">
                  <p className="text-gray-700 mb-2">
                    <strong>Formula:</strong> CO₂e = CH₄ mass × GWP
                  </p>
                  <p className="text-gray-700 mb-4">
                    <strong>Example:</strong> 1 ton of CH₄ × 28 = 28 tons CO₂e
                  </p>
                  <p className="text-gray-600 text-xs">
                    This means releasing 1 ton of methane has the same warming effect as releasing 28 tons of CO₂ over 100 years.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Why Methane Matters
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  While methane is shorter-lived than CO₂, its high warming potential means that reducing methane emissions can have a rapid positive impact on slowing global warming. Sources of methane include:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4 ml-4">
                  <li>Livestock and agriculture (40% of methane emissions)</li>
                  <li>Oil and gas production (23% of methane emissions)</li>
                  <li>Landfills and waste management (16% of methane emissions)</li>
                  <li>Natural sources like wetlands</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <h4 className="font-semibold text-gray-900 mb-2">Key Takeaway</h4>
                <p className="text-gray-700 text-sm">
                  When you see "tons of CO₂e" in greenhouse gas calculations, this includes the warming effects of all greenhouse gases converted to their CO₂ equivalent using GWP values. This allows us to compare different gases on a common scale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Calculation Methodologies
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Transportation Emissions
              </h3>
              <p className="text-gray-600 text-sm">
                Average passenger vehicle: 404 grams CO₂ per mile (EPA standard)
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Electricity Emissions
              </h3>
              <p className="text-gray-600 text-sm">
                U.S. average: 0.92 lbs CO₂ per kWh (varies by region and energy source)
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Dietary Impact
              </h3>
              <p className="text-gray-600 text-sm">
                Average annual emissions: 3.3 tons CO₂e (meat-based), 2.5 tons (vegetarian), 1.5 tons (vegan)
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
